import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Select from '../../components/common/Select';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ManualVotesTab from '../../components/votes/ManualVotesTab'; // ← adjust path if needed
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import voteService from '../../api/services/voteService';
import contestService from '../../api/services/contestService';
import { handleApiError, formatDate } from '../../utils/helpers';

// ─── Tab definitions ──────────────────────────────────────────────────────────
const TABS = {
  VOTES:   'votes',
  MANUAL:  'manual',
};

const VotesPage = () => {
  const { error: showError } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  // ── Active tab ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(TABS.VOTES);

  // ── Shared state (used by both tabs) ────────────────────────────────────────
  const [contests,        setContests]        = useState([]);
  const [selectedContest, setSelectedContest] = useState('');
  const [loadingContests, setLoadingContests] = useState(true);

  // ── Votes tab state ─────────────────────────────────────────────────────────
  const [votes,        setVotes]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dailyVotes,   setDailyVotes]   = useState([]);
  const [voteStats,    setVoteStats]    = useState(null);
  const [totalVotes,   setTotalVotes]   = useState(0);
  const [loadingTotal, setLoadingTotal] = useState(true);

  // ── Bootstrap ───────────────────────────────────────────────────────────────
  useEffect(() => { fetchContests(); }, []);

  useEffect(() => {
    if (selectedContest && activeTab === TABS.VOTES) {
      fetchVotes();
      fetchDailyVotes();
      fetchVoteStats();
    }
  }, [selectedContest, statusFilter, activeTab]);

  // ── Fetch helpers ───────────────────────────────────────────────────────────
  const fetchContests = async () => {
    try {
      setLoadingContests(true);
      const response = isSuperAdmin
        ? await contestService.getAllContests({ pageSize: 100 })
        : await contestService.getUserContests({ pageSize: 100 });

      if (response.success) {
        const list = response.data || response.contests || [];
        setContests(list);
        if (list.length > 0) setSelectedContest(list[0].id);
        fetchTotalVotes(list);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoadingContests(false);
    }
  };

  const fetchTotalVotes = async (list) => {
    try {
      setLoadingTotal(true);
      const results = await Promise.all(
        list.map(async (contest) => {
          try {
            const res = await voteService.getContestVotes(contest.id, {
              status: isSuperAdmin ? undefined : 'COMPLETED',
              pageSize: 1000,
            });
            return res.success ? (res.data || []) : [];
          } catch { return []; }
        })
      );
      const allVotes = results.flat();
      const completed = allVotes.filter(v => v.status === 'COMPLETED');
      setTotalVotes(completed.reduce((s, v) => s + (v.voteCount || 0), 0));
    } catch { setTotalVotes(0); }
    finally { setLoadingTotal(false); }
  };

  const fetchVotes = async () => {
    if (!selectedContest) return;
    try {
      setLoading(true);
      setError(null);
      const defaultStatus = !isSuperAdmin ? 'COMPLETED' : '';
      const response = await voteService.getContestVotes(selectedContest, {
        status: statusFilter || defaultStatus || undefined,
        pageSize: 100,
      });
      if (response.success) setVotes(response.data || []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyVotes = async () => {
    try {
      const response = await voteService.getDailyVotes(selectedContest, 7);
      if (response.success) {
        const data = (response.data || []).filter(v => v.status === 'COMPLETED');
        setDailyVotes(data);
      }
    } catch {}
  };

  const fetchVoteStats = async () => {
    try {
      const response = await voteService.getVoteStats(selectedContest);
      if (response.success) setVoteStats(response.data || null);
    } catch {}
  };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const completedVoteCount = votes
    .filter(v => v.status === 'COMPLETED')
    .reduce((s, v) => s + (v.voteCount || 0), 0);

  const totalVoteCount   = votes.reduce((s, v) => s + (v.voteCount || 0), 0);
  const uniqueVotersCount = votes.length > 0
    ? new Set(votes.map(v => v.voterEmail || v.voterPhone || v.userId).filter(Boolean)).size
    : 0;

  const selectedContestData = contests.find(c => c.id === selectedContest);

  const getStatusColor = (status) => ({
    COMPLETED: 'bg-green-100 text-green-800',
    PENDING:   'bg-yellow-100 text-yellow-800',
    FAILED:    'bg-red-100 text-red-800',
  }[status] || 'bg-gray-100 text-gray-800');

  // ── Loading shell ────────────────────────────────────────────────────────────
  if (loadingContests && contests.length === 0) {
    return (
      <DashboardLayout title={isSuperAdmin ? 'All Votes' : 'My Votes'}>
        <div className="flex items-center justify-center min-h-96">
          <Loader size="lg" text="Loading votes..." />
        </div>
      </DashboardLayout>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout
      title={isSuperAdmin ? 'All Votes' : 'My Votes'}
      subtitle={
        isSuperAdmin
          ? 'View all votes in the system'
          : 'View and manage votes for your contests'
      }
    >
      {/* ── Total votes banner (Company Admin only) ───────────────────────── */}
      {!isSuperAdmin && (
        <Card className="mb-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl">
          <div className="text-center py-8">
            <div className="flex items-center justify-center mb-3">
              <svg className="w-12 h-12 text-white opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold mb-2 opacity-90">Total Completed Votes</h2>
            {loadingTotal ? (
              <div className="flex items-center justify-center gap-3">
                <Loader size="md" className="text-white" />
                <span className="text-xl">Loading...</span>
              </div>
            ) : (
              <>
                <p className="text-7xl font-bold mb-2">{completedVoteCount.toLocaleString()}</p>
                <p className="text-lg opacity-90">
                  Across all your {contests.length} contest{contests.length !== 1 ? 's' : ''}
                </p>
                <div className="mt-4 pt-4 border-t border-white border-opacity-20">
                  <p className="text-sm opacity-75">✓ Paid votes only • Real-time updates</p>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* ── Contest selector + status filter ─────────────────────────────── */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Select Contest"
            value={selectedContest}
            onChange={(e) => setSelectedContest(e.target.value)}
            options={contests.map(c => ({ value: c.id, label: c.title }))}
            placeholder="Select a contest"
          />

          {isSuperAdmin ? (
            <Select
              label="Filter by Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: '',          label: 'All Statuses' },
                { value: 'COMPLETED', label: 'Completed'    },
                { value: 'PENDING',   label: 'Pending'      },
                { value: 'FAILED',    label: 'Failed'       },
              ]}
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <div className="mt-1 block w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-600">
                Showing Completed Votes Only
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* ── Tab navigation ────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          {/* Votes tab */}
          <button
            onClick={() => setActiveTab(TABS.VOTES)}
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === TABS.VOTES
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Vote Transactions
            {votes.length > 0 && (
              <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                {votes.length}
              </span>
            )}
          </button>

          {/* Manual votes tab */}
          <button
            onClick={() => setActiveTab(TABS.MANUAL)}
            className={`py-3 px-1 border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === TABS.MANUAL
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Manual Votes
            <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-0.5 rounded-full">
              Audit Log
            </span>
          </button>
        </nav>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: VOTE TRANSACTIONS
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === TABS.VOTES && (
        <>
          {selectedContest && (
            <>
              {/* Stats cards */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Contest Statistics — {selectedContestData?.title}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                {[
                  {
                    label: isSuperAdmin ? 'Total Votes Cast' : 'Completed Votes',
                    value: completedVoteCount,
                    sub:   !isSuperAdmin ? 'This contest only' : null,
                    color: 'blue',
                    icon:  'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
                  },
                  {
                    label: 'Unique Voters',
                    value: uniqueVotersCount,
                    color: 'green',
                    icon:  'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
                  },
                  {
                    label: 'Vote Transactions',
                    value: votes.length,
                    color: 'purple',
                    icon:  'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                  },
                  {
                    label: 'Avg. Votes/Person',
                    value: uniqueVotersCount > 0 ? (totalVoteCount / uniqueVotersCount).toFixed(1) : 0,
                    color: 'orange',
                    icon:  'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                  },
                ].map(({ label, value, sub, color, icon }) => (
                  <Card key={label}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{label}</p>
                        <p className={`text-3xl font-bold text-${color}-600`}>{value}</p>
                        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
                      </div>
                      <div className={`bg-${color}-100 p-3 rounded-lg`}>
                        <svg className={`w-8 h-8 text-${color}-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon} />
                        </svg>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Daily activity bar chart */}
              {dailyVotes.length > 0 && (
                <Card className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Daily Voting Activity (Last 7 Days)
                    {!isSuperAdmin && (
                      <span className="text-sm text-gray-500 ml-2">— Completed votes only</span>
                    )}
                  </h3>
                  <div className="space-y-3">
                    {dailyVotes.map((day, index) => {
                      const maxVotes  = Math.max(...dailyVotes.map(d => Number(d.votes) || 0));
                      const dayVotes  = Number(day.votes) || 0;
                      const dayTxns   = Number(day.transactions) || 0;
                      const pct       = maxVotes > 0 ? (dayVotes / maxVotes) * 100 : 0;
                      return (
                        <div key={index} className="flex items-center gap-4">
                          <div className="w-32 text-sm text-gray-600">
                            {new Date(day.date).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                            })}
                          </div>
                          <div className="flex-1">
                            <div className="relative h-10 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                                style={{ width: `${pct}%` }}
                              />
                              <div className="absolute inset-0 flex items-center justify-between px-4">
                                <span className="text-sm font-semibold text-gray-700">
                                  {dayVotes} vote{dayVotes !== 1 ? 's' : ''}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {dayTxns} transaction{dayTxns !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </>
          )}

          {error && <ErrorMessage message={error} onRetry={fetchVotes} className="mb-6" />}

          {/* Votes table */}
          {loading ? (
            <Card>
              <div className="flex items-center justify-center py-12">
                <Loader size="md" text="Loading votes..." />
              </div>
            </Card>
          ) : votes.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {isSuperAdmin ? 'No Votes Yet' : 'No Completed Votes Yet'}
                </h3>
                <p className="text-gray-600">
                  {selectedContestData
                    ? `"${selectedContestData.title}" hasn't received any ${!isSuperAdmin ? 'completed ' : ''}votes yet`
                    : `This contest hasn't received any ${!isSuperAdmin ? 'completed ' : ''}votes yet`}
                </p>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Vote Details {selectedContestData && `— ${selectedContestData.title}`}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Showing {votes.length} {!isSuperAdmin && 'completed '}vote transaction{votes.length !== 1 ? 's' : ''}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Voter Information', 'Contest Details', 'Contestant', 'Category', 'Votes', 'Status', 'Date'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {votes.map(vote => (
                      <tr key={vote.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {vote.voterName}
                          </div>
                          {vote.voterEmail && (
                            <div className="text-sm text-gray-500 mt-1">{vote.voterEmail}</div>
                          )}
                          {vote.voterPhone && (
                            <div className="text-xs text-gray-400 mt-1">{vote.voterPhone}</div>
                          )}
                          {/* Manual badge */}
                          {vote.isManual && (
                            <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                              Manual
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {vote.contest?.title || selectedContestData?.title || 'N/A'}
                          </div>
                          {vote.contest?.host && (
                            <div className="text-xs text-gray-500 mt-1">by {vote.contest.host.name}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {vote.contestant?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {vote.category?.name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold bg-blue-100 text-blue-800">
                            {vote.voteCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(vote.status)}`}>
                            {vote.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(vote.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          TAB: MANUAL VOTES
      ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === TABS.MANUAL && (
        <ManualVotesTab
          selectedContest={selectedContest}
          contests={contests}
        />
      )}
    </DashboardLayout>
  );
};

export default VotesPage;